export type DomainRouterApiError = {
  message: string;
  code?: string;
};

const INVALID_ERROR_TEXT = '[object Object]';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed && trimmed !== INVALID_ERROR_TEXT ? trimmed : null;
};

const readErrorsArrayMessage = (value: unknown): string | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  const joined = value
    .map((entry) => {
      if (!isRecord(entry)) {
        return null;
      }
      return readText(entry.message);
    })
    .filter((entry): entry is string => Boolean(entry))
    .join('; ');
  return joined || null;
};

const readCode = (value: unknown, depth = 0): string | null => {
  if (depth > 3 || !isRecord(value)) {
    return null;
  }
  const candidateKeys = ['code', 'errorCode', 'errorType'];
  for (const key of candidateKeys) {
    const candidate = readText(value[key]);
    if (candidate) return candidate;
  }
  for (const nestedKey of ['data', 'error', 'extensions', 'cause']) {
    const candidate = readCode(value[nestedKey], depth + 1);
    if (candidate) return candidate;
  }
  const errors = Array.isArray(value.errors) ? value.errors : [];
  for (const error of errors) {
    const candidate = readCode(error, depth + 1);
    if (candidate) return candidate;
  }
  return null;
};

export const readDomainRouterErrorMessage = (
  error: unknown,
  fallback = 'Unknown error',
): string => {
  if (error instanceof Error) {
    return readText(error.message) ?? fallback;
  }

  const directText = readText(error);
  if (directText) {
    return directText;
  }

  if (!isRecord(error)) {
    return fallback;
  }

  const directMessage = readText(error.message);
  if (directMessage) {
    return directMessage;
  }

  const nestedDataMessage = isRecord(error.data) ? readText(error.data.message) : null;
  if (nestedDataMessage) {
    return nestedDataMessage;
  }

  const nestedErrorMessage = isRecord(error.error) ? readText(error.error.message) : null;
  if (nestedErrorMessage) {
    return nestedErrorMessage;
  }

  const graphqlErrorsMessage = readErrorsArrayMessage(error.errors);
  if (graphqlErrorsMessage) {
    return graphqlErrorsMessage;
  }

  const nestedDataErrorsMessage = isRecord(error.data)
    ? readErrorsArrayMessage(error.data.errors)
    : null;
  if (nestedDataErrorsMessage) {
    return nestedDataErrorsMessage;
  }

  return fallback;
};

export const readDomainRouterErrorMessageWhenPresent = (
  error: unknown,
  isError: boolean,
  fallback = 'Unknown error',
): string | null => (
  !isError
    ? null
    : readDomainRouterErrorMessage(error, fallback)
);

export const toDomainRouterApiError = (
  error: unknown,
  fallback = 'Unknown error',
): DomainRouterApiError => {
  const code = readCode(error);
  return {
    message: readDomainRouterErrorMessage(error, fallback),
    ...(code ? { code } : {}),
  };
};
